import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import NavHeader from "@/components/NavHeader";
import DiceRoller from "@/components/DiceRoller";
import StatBlock from "@/components/StatBlock";
import ArrayEditor from "@/components/ArrayEditor";
import Shop from "@/components/Shop";
import CyberdeckLoadout from "@/components/CyberdeckLoadout";
import ClassPicker from "@/components/ClassPicker";
import ShareDialog from "@/components/ShareDialog";
import Library from "@/components/Library";
import Bestiary from "@/components/Bestiary";
import CombatArena from "@/components/CombatArena";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { loadLocalCharacters, upsertLocalCharacter } from "@/lib/storage";
import { STAT_KEYS, statSum, validate, STAT_POOL_LIMIT, STARTING_SATS_MAX, HP_MAX_CAP, CLASSES, emptyCharacter, rollStatForClass, rollHackerStarterApp, storageCapacity, storageUsed, weaponDamageDie, credsFormulaFor, debtFormulaFor, formulaLabel, rollFormula, rollClassLoadout, hpDieFor, rollHpForClass, rollNanoStarterPowers, rollStartingGear, rollPunkName, glitchDieFor, rollGlitchesForClass } from "@/lib/character";
import { EQUIPMENT } from "@/lib/equipment";
import { toast } from "sonner";
import { Save, ArrowLeft, AlertTriangle, Cloud, HardDrive, ShoppingCart, Package, Skull, Swords, Menu, X } from "lucide-react";
import TickBoxes from "@/components/TickBoxes";
import BrokenPrompt from "@/components/BrokenPrompt";

export default function CharacterEditor() {
  const { id, chronId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const isChronicleGmView = !!chronId;
  const [character, setCharacter] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [brokenOpen, setBrokenOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [bestiaryOpen, setBestiaryOpen] = useState(false);
  const [arenaOpen, setArenaOpen] = useState(false);
  const [roster, setRoster] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    setBusy(true);
    if (isChronicleGmView) {
      try {
        const r = await api.get(`/chronicles/${chronId}/characters/${id}`);
        setCharacter(r.data);
      } catch {
        toast.error("Cannot open this punk — GM access denied");
        navigate(`/chronicle/${chronId}`, { replace: true });
        return;
      }
    } else if (user) {
      try {
        const r = await api.get(`/characters/${id}`);
        setCharacter(r.data);
      } catch {
        toast.error("Character not found in cloud, checking local…");
        const local = loadLocalCharacters().find((c) => c.id === id);
        setCharacter(local || { ...emptyCharacter(), id });
      }
    } else {
      const local = loadLocalCharacters().find((c) => c.id === id);
      setCharacter(local || { ...emptyCharacter(), id });
    }
    setBusy(false);
  }, [id, user, isChronicleGmView, chronId, navigate]);

  useEffect(() => { if (!loading) load(); }, [load, loading]);

  // Load roster for arena on demand
  const openArena = async () => {
    if (user) {
      try {
        const r = await api.get("/characters");
        setRoster(r.data || []);
      } catch {
        setRoster(loadLocalCharacters());
      }
    } else {
      setRoster(loadLocalCharacters());
    }
    setArenaOpen(true);
  };

  const persist = useCallback(async (c) => {
    setSaving(true);
    if (isChronicleGmView) {
      try {
        const { id: _id, ...rest } = c;
        await api.put(`/chronicles/${chronId}/characters/${_id}`, rest);
      } catch { toast.error("GM save failed"); }
    } else if (user) {
      try {
        const { id: _id, ...rest } = c;
        await api.post("/characters", { id: _id, ...rest });
      } catch { toast.error("Cloud save failed — keeping local backup"); upsertLocalCharacter(c); }
    } else {
      upsertLocalCharacter(c);
    }
    setSaving(false);
  }, [user, isChronicleGmView, chronId]);

  const patch = (updates) => {
    setCharacter((prev) => {
      const next = { ...prev, ...updates };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(next), 700);
      return next;
    });
  };

  const warnings = useMemo(() => (character ? validate(character) : []), [character]);
  const pool = character ? statSum(character) : 0;

  // Auto-open Broken prompt when HP drops to 0 (once per drop).
  const wasBrokenRef = useRef(false);
  useEffect(() => {
    if (!character) return;
    if ((character.hp_current || 0) <= 0 && (character.hp_max || 0) > 0) {
      if (!wasBrokenRef.current) {
        wasBrokenRef.current = true;
        setBrokenOpen(true);
      }
    } else {
      wasBrokenRef.current = false;
    }
  }, [character?.hp_current, character?.hp_max, character]);

  // Keep Burned Hacker's starter cyberdeck slots in sync with Knowledge (book rule: KNO+4 slots).
  const lastHackerSlotsRef = useRef(null);
  useEffect(() => {
    if (!character) return;
    if (character.char_class !== "The Burned Hacker") return;
    const decks = character.cybertech || [];
    const kitIdx = decks.findIndex((d) => d && d.hacker_kit);
    if (kitIdx < 0) return;
    const expected = Math.max(1, (character.knowledge || 0) + 4);
    if (decks[kitIdx].slots === expected) return;
    if (lastHackerSlotsRef.current === expected) return;
    lastHackerSlotsRef.current = expected;
    const next = decks.map((d, i) => (i === kitIdx ? { ...d, slots: expected } : d));
    patch({ cybertech: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character?.knowledge, character?.char_class, character?.cybertech]);

  if (busy || !character) {
    return (
      <div className="min-h-screen scanlines">
        <NavHeader />
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
          <div className="font-mono text-sm text-[color:var(--text-muted)]">LOADING PUNK…</div>
        </div>
      </div>
    );
  }

  const listPatch = (field, next) => patch({ [field]: next });

  // Burned Hacker starter kit: cyberdeck with Knowledge+4 slots + a random App loaded onto it.
  const applyHackerStarter = (charBefore) => {
    const deckId = globalThis.crypto?.randomUUID?.() || `deck_${Date.now()}`;
    const kSlots = Math.max(1, (charBefore.knowledge || 0) + 4);
    const deck = { id: deckId, text: `Cyberdeck (KNO+4, starter)`, price: 0, is_deck: true, slots: kSlots, hacker_kit: true };
    const rolled = rollHackerStarterApp();
    const app = { id: rolled.id, text: rolled.text, loaded_to: deckId };
    toast.success("STARTER KIT INSTALLED", { description: `d6 → ${rolled.roll} · ${rolled.text}` });
    return {
      cybertech: [...(charBefore.cybertech || []), deck],
      apps: [...(charBefore.apps || []), app],
    };
  };

  const changeClass = (name) => {
    // No-op when the user picks the same class (or the placeholder)
    if (!name || name === character.char_class) {
      patch({ char_class: name });
      return;
    }

    // Detect if the sheet has meaningful data that would be wiped
    const hasData =
      (character.weapons || []).length ||
      (character.armor || []).length ||
      (character.cybertech || []).length ||
      (character.apps || []).length ||
      (character.gear || []).length ||
      (character.nano_powers || []).length ||
      (character.presence || 0) ||
      (character.strength || 0) ||
      (character.agility || 0) ||
      (character.toughness || 0) ||
      (character.knowledge || 0) ||
      (character.sats || 0) !== 0 ||
      (character.debt || 0) ||
      (character.glitches_count || 0) ||
      (character.description || "").trim() ||
      (character.bad_habits || "").trim() ||
      (character.worldly_possessions || "").trim() ||
      (character.notes || "").trim();

    if (hasData) {
      if (!window.confirm("CHANGING CLASS RESETS THE SHEET (name is kept). Continue?")) return;
    }

    // Reset to a fresh character, preserving id + name only
    const fresh = emptyCharacter();
    const reset = {
      ...fresh,
      id: character.id,
      name: character.name,
      char_class: name,
      created_at: character.created_at,
      updated_at: new Date().toISOString(),
    };

    // Roll new class loadout (weapon + armor)
    const loadout = rollClassLoadout(name, EQUIPMENT);
    if (loadout) {
      reset.weapons = [loadout.weapon];
      reset.armor = [loadout.armor];
      toast.success("CLASS LOADOUT GRANTED", {
        description: `${loadout.picked.weapon} + ${loadout.picked.armor}`,
      });
    }
    // Everyone rolls 2 starting gear pieces
    const gearPicks = rollStartingGear(2);
    reset.gear = gearPicks;
    toast.success("STARTING GEAR ROLLED", {
      description: gearPicks.map((g) => g.text.split(" — ")[0]).join(" · "),
    });
    // Class-specific glitches roll (Gang-Goon d2, Nanomancer d4, Cyberslasher d3, Hacker d2, Corp-Killer d2, Gear-Head d4)
    const gl = rollGlitchesForClass(name);
    reset.glitches_count = gl.value;
    toast(`GLITCHES · ${gl.value}`, { description: `d${gl.die} = ${gl.value}` });
    // Burned Hacker bonus: cyberdeck + d6 starter app
    if (name === "The Burned Hacker") {
      const hackerKit = applyHackerStarter({ cybertech: [], apps: [] });
      reset.cybertech = hackerKit.cybertech;
      reset.apps = hackerKit.apps;
    }
    // Shunned Nanomancer bonus: 2 random nano powers
    if (name === "The Shunned Nanomancer") {
      const powers = rollNanoStarterPowers(2);
      reset.nano_powers = powers;
      toast.success("NANO POWERS AWAKENED", {
        description: powers.map((p) => p.text.split(" — ")[0]).join(" · "),
      });
    }

    setCharacter(reset);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(reset), 700);
  };

  const rollStat = (statKey, statLabel) => {
    if (!character.char_class) {
      toast.error("PICK A CLASS FIRST", { description: "Class determines your roll formula." });
      return;
    }
    const res = rollStatForClass(statKey, character.char_class);
    const rolled = new Set([...(character.stats_rolled || []), statKey]);
    const updates = { [statKey]: res.modifier, stats_rolled: Array.from(rolled) };

    // When all 5 stats are rolled, auto-roll HP from class HP die + Toughness
    if (rolled.size === 5) {
      const nextToughness = statKey === "toughness" ? res.modifier : (character.toughness || 0);
      const hp = rollHpForClass(character.char_class, nextToughness);
      updates.hp_max = hp.total;
      updates.hp_current = hp.total;
      const dice = res.dice.map((d) => `${d.label}=${d.value}`).join(" ");
      toast.success(`${statLabel} → ${res.modifier >= 0 ? "+" : ""}${res.modifier}`, {
        description: `${dice} · sum ${res.sum}`,
      });
      toast.success(`HP → ${hp.total}`, {
        description: `d${hp.sides}=${hp.die} + TOU ${nextToughness >= 0 ? "+" : ""}${nextToughness}`,
      });
      patch(updates);
      return;
    }

    const dice = res.dice.map((d) => `${d.label}=${d.value}`).join(" ");
    toast.success(`${statLabel} → ${res.modifier >= 0 ? "+" : ""}${res.modifier}`, {
      description: `${dice} · sum ${res.sum}`,
    });
    patch(updates);
  };

  const rerollHp = () => {
    if (!character.char_class) {
      toast.error("PICK A CLASS FIRST");
      return;
    }
    const hp = rollHpForClass(character.char_class, character.toughness || 0);
    patch({ hp_max: hp.total, hp_current: hp.total });
    toast.success(`HP → ${hp.total}`, {
      description: `d${hp.sides}=${hp.die} + TOU ${(character.toughness || 0) >= 0 ? "+" : ""}${character.toughness || 0}`,
    });
  };
  const classSpec = character.char_class ? CLASSES.find((c) => c.name === character.char_class) : null;
  const hintFor = (statKey) => {
    if (!classSpec) return "PICK CLASS";
    const s = classSpec.stat_rolls?.[statKey] || {};
    const parts = ["3d6"];
    if (typeof s.mod === "number" && s.mod !== 0) {
      parts.push(s.mod > 0 ? `+${s.mod}` : `${s.mod}`);
    }
    return parts.join(" ");
  };

  // Sell an item back for half CREDS
  const sellItem = (field, item, idx) => {
    const price = item?.price || 0;
    if (!price) {
      toast.error("NOT FOR SALE", { description: "Only items with a market price can be sold back." });
      return;
    }
    const refund = Math.floor(price / 2);
    const nextList = (character[field] || []).filter((_, i) => i !== idx);
    // If selling a cyberdeck: also unload any apps that were loaded onto it
    const updates = { [field]: nextList, sats: (character.sats || 0) + refund };
    if (field === "cybertech" && item?.is_deck && item?.id) {
      updates.apps = (character.apps || []).map((a) => (a.loaded_to === item.id ? { ...a, loaded_to: null } : a));
    }
    // If selling a backpack while over new capacity, warn
    if (field === "gear" && /backpack/i.test(item.text || "")) {
      const gearLen = nextList.length;
      const newCap = 4; // no more backpack
      if (gearLen > newCap) {
        toast("STORAGE OVERLOAD", { description: `You're now carrying ${gearLen}/${newCap}. Drop something.` });
      }
    }
    patch(updates);
    toast.success(`SOLD · ${item.text}`, { description: `+${refund}¤ (half of ${price}¤)` });
  };

  // CREDS roll: class-specific formula (defaults 3d6×10)
  const rollCreds = () => {
    const f = credsFormulaFor(character.char_class);
    if ((character.sats || 0) > 0) {
      if (!window.confirm("REROLL STARTING CREDS? This overwrites current CREDS.")) return;
    }
    const r = rollFormula(f);
    patch({ sats: r.total });
    toast.success(`STARTING CREDS · ${r.total.toLocaleString()}¤`, {
      description: `${formulaLabel(f)} → [${r.rolls.join("+")}] = ${r.sum} × ${f.mult}`,
    });
  };

  // DEBT roll: class-specific formula (defaults 3d6×1000)
  const rollDebt = () => {
    const f = debtFormulaFor(character.char_class);
    if ((character.debt || 0) > 0) {
      if (!window.confirm("REROLL DEBT? This overwrites your current DEBT.")) return;
    }
    const r = rollFormula(f);
    patch({ debt: r.total });
    toast.success(`DEBT · ${r.total.toLocaleString()}¤`, {
      description: `${formulaLabel(f)} → [${r.rolls.join("+")}] = ${r.sum} × ${f.mult}`,
    });
  };

  const actionButtons = (extraClass = "") => (
    <>
      <button data-testid="open-share-btn" onClick={() => { setShareOpen(true); setMobileMenuOpen(false); }} className={`cy-btn cy-btn-cyan text-xs ${extraClass}`}>
        SHARE
      </button>
      <button data-testid="open-library-btn" onClick={() => { setLibraryOpen(true); setMobileMenuOpen(false); }} className={`cy-btn cy-btn-cyan text-xs ${extraClass}`}>
        LIBRARY
      </button>
      <button data-testid="open-bestiary-btn" onClick={() => { setBestiaryOpen(true); setMobileMenuOpen(false); }} className={`cy-btn cy-btn-magenta text-xs ${extraClass}`}>
        <Skull size={14} className="mr-1" /> BESTIARY
      </button>
      <button data-testid="open-arena-btn" onClick={() => { openArena(); setMobileMenuOpen(false); }} className={`cy-btn cy-btn-solid-yellow text-xs ${extraClass}`}>
        <Swords size={14} className="mr-1" /> ARENA
      </button>
      <button data-testid="open-shop-btn" onClick={() => { setShopOpen(true); setMobileMenuOpen(false); }} className={`cy-btn cy-btn-magenta text-xs ${extraClass}`}>
        <ShoppingCart size={14} className="mr-1" /> BLACK MARKET
      </button>
      <button onClick={() => { persist(character); setMobileMenuOpen(false); }} className={`cy-btn cy-btn-solid-yellow text-xs ${extraClass}`} data-testid="save-now-btn">
        <Save size={14} className="mr-1" /> SAVE NOW
      </button>
    </>
  );

  return (
    <div className="min-h-screen scanlines pb-24">
      <NavHeader />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 relative">
          <button data-testid="back-btn" onClick={() => navigate("/dashboard")} className="cy-btn cy-btn-cyan text-xs">
            <ArrowLeft size={14} className="mr-1" /> BACK
          </button>
          {/* Desktop: inline actions */}
          <div className="hidden sm:flex items-center gap-2 flex-wrap justify-end">
            {actionButtons()}
            {user ? <span className="tape tape-cyan flex items-center gap-1 text-[10px]"><Cloud size={12} /> CLOUD</span> : <span className="tape flex items-center gap-1 text-[10px]"><HardDrive size={12} /> LOCAL</span>}
            <span data-testid="save-status" className="font-mono text-xs uppercase tracking-widest text-[color:var(--text-muted)]">
              {saving ? "SAVING…" : "SAVED"}
            </span>
          </div>
          {/* Mobile: single toggle */}
          <div className="flex sm:hidden items-center gap-2">
            {user ? <span className="tape tape-cyan flex items-center gap-1 text-[10px]"><Cloud size={12} /> CLOUD</span> : <span className="tape flex items-center gap-1 text-[10px]"><HardDrive size={12} /> LOCAL</span>}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              className="cy-btn cy-btn-magenta text-xs"
            >
              {mobileMenuOpen ? <X size={14} className="mr-1" /> : <Menu size={14} className="mr-1" />} MENU
            </button>
          </div>
          {/* Mobile slide-down drawer */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
              className="sm:hidden absolute top-full inset-x-0 z-40 mt-2 panel p-3 grid grid-cols-2 gap-2"
              data-testid="mobile-menu-panel"
            >
              {actionButtons("w-full justify-center")}
              <div className="col-span-2 flex items-center justify-between px-1">
                <span data-testid="save-status" className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                  {saving ? "SAVING…" : "SAVED"}
                </span>
              </div>
            </motion.div>
          )}
        </div>

        {/* Character header */}
        <div className="panel p-4 sm:p-6 mb-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
            <div className="lg:col-span-6">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">NAME</label>
                <button
                  data-testid="roll-name-btn"
                  onClick={() => {
                    const n = rollPunkName();
                    patch({ name: n });
                    toast.success(`STREET NAME · ${n}`);
                  }}
                  title="Roll a random punk street name"
                  className="cy-btn text-[9px] px-2 py-0.5 min-h-0"
                >
                  ROLL NAME
                </button>
              </div>
              <input
                data-testid="char-name-input"
                value={character.name}
                onChange={(e) => patch({ name: e.target.value })}
                className="cy-input font-display font-black text-3xl sm:text-4xl uppercase tracking-tighter mt-1"
                style={{ minHeight: 60 }}
              />
            </div>
            <div className="lg:col-span-4">
              <label className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">CLASS</label>
              <ClassPicker
                value={character.char_class}
                onChange={(name) => changeClass(name)}
              />
            </div>
            <div className="lg:col-span-2 flex gap-2 items-end min-w-0">
              <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">HP</label>
                {character.char_class && (
                  <button
                    data-testid="roll-hp-btn"
                    onClick={rerollHp}
                    title={`Roll d${hpDieFor(character.char_class)} + Toughness`}
                    className="cy-btn text-[9px] px-2 py-0.5 min-h-0"
                  >
                    ROLL d{hpDieFor(character.char_class)}+TOU
                  </button>
                )}
              </div>
                <div className="flex gap-1 mt-1">
                  <input data-testid="hp-current-input" type="number" value={character.hp_current} onChange={(e) => patch({ hp_current: parseInt(e.target.value || "0", 10) })} className="cy-input text-center" />
                  <span className="self-center text-[color:var(--text-muted)]">/</span>
                  <input data-testid="hp-max-input" type="number" value={character.hp_max} onChange={(e) => patch({ hp_max: parseInt(e.target.value || "0", 10) })} className="cy-input text-center" />
                </div>
                {character.hp_max > 0 && (
                  <div className="mt-2">
                    <TickBoxes
                      testKeyPrefix="hp"
                      count={Math.min(character.hp_max, 12)}
                      value={Math.max(0, Math.min(character.hp_max, character.hp_max - (character.hp_current || 0)))}
                      onChange={(damage) => patch({ hp_current: character.hp_max - damage })}
                      color="magenta"
                      label="DMG"
                    />
                  </div>
                )}
                {(character.hp_current || 0) <= 0 && character.hp_max > 0 && (
                  <button
                    data-testid="open-broken-btn"
                    onClick={() => setBrokenOpen(true)}
                    className="cy-btn cy-btn-magenta text-[10px] py-1 mt-2 min-h-0"
                  >
                    <Skull size={12} className="mr-1" /> ROLL BROKEN d8
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 pt-3 border-t-2 border-dashed border-[color:var(--border-hard)]">
            <TickBoxes
              testKeyPrefix="glitch"
              count={4}
              value={character.glitches_count || 0}
              onChange={(v) => patch({ glitches_count: v })}
              color="cyan"
              label="GLITCHES"
            />
            <div className="flex items-center gap-2 ml-auto" data-testid="storage-indicator">
              <Package size={14} className="text-[color:var(--accent-yellow)]" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
                STORAGE <span className={storageUsed(character) > storageCapacity(character) ? "text-[color:var(--accent-magenta)] font-bold" : "text-[color:var(--accent-yellow)] font-bold"}>{storageUsed(character)}</span>/{storageCapacity(character)}
              </span>
            </div>
          </div>
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="panel panel-magenta p-3 mb-4 flex items-start gap-2" data-testid="warnings-panel">
            <AlertTriangle className="text-[color:var(--accent-magenta)] shrink-0 mt-0.5" size={18} />
            <div className="font-mono text-xs uppercase tracking-widest">
              {warnings.map((w, i) => <div key={i}>▸ {w}</div>)}
            </div>
          </div>
        )}

        {/* Stats + Currency */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-2">
              <div className="font-display uppercase text-lg font-bold tracking-tight text-[color:var(--accent-yellow)]">// STATS</div>
              <div className="text-xs font-mono uppercase tracking-widest">
                POOL <span data-testid="stat-pool-value" className={pool > STAT_POOL_LIMIT ? "text-[color:var(--accent-magenta)] font-bold" : "text-[color:var(--accent-yellow)] font-bold"}>{pool}</span> / {STAT_POOL_LIMIT}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {STAT_KEYS.map((s) => (
                <StatBlock
                  key={s.key}
                  label={s.label}
                  abbr={s.abbr}
                  accent={s.accent}
                  testKey={s.key}
                  value={character[s.key]}
                  onChange={(v) => patch({ [s.key]: v })}
                  onRoll={() => rollStat(s.key, s.label)}
                  rollHint={hintFor(s.key)}
                />
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 gap-3">
            <div className="panel panel-yellow p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">CREDS</div>
                <button
                  data-testid="roll-creds-btn"
                  onClick={rollCreds}
                  title={`Roll ${formulaLabel(credsFormulaFor(character.char_class))}`}
                  className="cy-btn text-[9px] px-2 py-0.5 min-h-0"
                >
                  ROLL {formulaLabel(credsFormulaFor(character.char_class))}
                </button>
              </div>
              <input
                data-testid="sats-input"
                type="number"
                value={character.sats}
                onChange={(e) => patch({ sats: parseInt(e.target.value || "0", 10) })}
                className="cy-input font-display text-3xl font-black mt-1 text-[color:var(--accent-yellow)] border-0 px-0"
              />
              <div className="text-[10px] font-mono text-[color:var(--text-muted)]">MAX {STARTING_SATS_MAX}</div>
            </div>
            <div className="panel panel-magenta p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">DEBT</div>
                <button
                  data-testid="roll-debt-btn"
                  onClick={rollDebt}
                  title={`Roll ${formulaLabel(debtFormulaFor(character.char_class))}`}
                  className="cy-btn cy-btn-magenta text-[9px] px-2 py-0.5 min-h-0"
                >
                  ROLL {formulaLabel(debtFormulaFor(character.char_class))}
                </button>
              </div>
              <input
                data-testid="debt-input"
                type="number"
                value={character.debt}
                onChange={(e) => patch({ debt: parseInt(e.target.value || "0", 10) })}
                className="cy-input font-display text-3xl font-black mt-1 text-[color:var(--accent-magenta)] border-0 px-0"
              />
              <div className="text-[10px] font-mono text-[color:var(--text-muted)]">OWED</div>
            </div>
            <div className="col-span-2 panel p-3 text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
              HP MAX SOFT CAP: {HP_MAX_CAP}
            </div>
          </div>
        </div>

        {/* Arrays */}
        <CyberdeckLoadout character={character} onChange={(updates) => patch(updates)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <ArrayEditor
            items={character.weapons || []}
            onChange={(v) => listPatch("weapons", v)}
            label="WEAPONS"
            accent="magenta"
            testKey="weapons"
            itemActions={[
              {
                label: "STRIKE",
                testKey: "strike",
                color: "magenta",
                title: "Auto-roll this weapon's damage die",
                visible: (it) => !!weaponDamageDie(it),
                onClick: (it) => {
                  const d = weaponDamageDie(it);
                  if (!d) { toast.error("NO DAMAGE DIE"); return; }
                  window.dispatchEvent(new CustomEvent("cyborg:roll", {
                    detail: { label: `${it.text || "WEAPON"} STRIKE`, sides: d.sides, count: d.count },
                  }));
                },
              },
              {
                label: "SELL",
                testKey: "sell",
                color: "yellow",
                title: "Sell back for half CREDS",
                visible: (it) => (it?.price || 0) > 0,
                onClick: (it, idx) => sellItem("weapons", it, idx),
              },
            ]}
          />
          <ArrayEditor
            items={character.armor || []}
            onChange={(v) => listPatch("armor", v)}
            label="ARMOR"
            accent="cyan"
            testKey="armor"
            itemActions={[
              {
                label: "SELL",
                testKey: "sell",
                color: "yellow",
                title: "Sell back for half CREDS",
                visible: (it) => (it?.price || 0) > 0,
                onClick: (it, idx) => sellItem("armor", it, idx),
              },
            ]}
          />
          <ArrayEditor
            items={character.gear || []}
            onChange={(v) => listPatch("gear", v)}
            label="GEAR"
            accent="yellow"
            testKey="gear"
            capacity={storageCapacity(character)}
            itemActions={[
              {
                label: "SELL",
                testKey: "sell",
                color: "magenta",
                title: "Sell back for half CREDS",
                visible: (it) => (it?.price || 0) > 0,
                onClick: (it, idx) => sellItem("gear", it, idx),
              },
            ]}
          />
          <ArrayEditor
            items={character.cybertech || []}
            onChange={(v) => listPatch("cybertech", v)}
            label="CYBERTECH"
            accent="magenta"
            testKey="cybertech"
            itemActions={[
              {
                label: "SELL",
                testKey: "sell",
                color: "yellow",
                title: "Sell back for half CREDS",
                visible: (it) => (it?.price || 0) > 0,
                onClick: (it, idx) => sellItem("cybertech", it, idx),
              },
            ]}
          />
          <ArrayEditor items={character.nano_powers || []} onChange={(v) => listPatch("nano_powers", v)} label="NANO POWERS" accent="cyan" testKey="nano" />
          <ArrayEditor items={character.apps || []} onChange={(v) => listPatch("apps", v)} label="APPS" accent="cyan" testKey="apps" />
        </div>

        {/* Text fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {[
            { k: "description", l: "DESCRIPTION" },
            { k: "bad_habits", l: "BAD HABITS" },
            { k: "worldly_possessions", l: "WORLDLY POSSESSIONS" },
            { k: "notes", l: "NOTES" },
          ].map((f) => (
            <div key={f.k} className="panel p-4">
              <label className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">{f.l}</label>
              <textarea
                data-testid={`text-${f.k}`}
                value={character[f.k] || ""}
                onChange={(e) => patch({ [f.k]: e.target.value })}
                className="cy-input mt-1"
                rows={4}
              />
            </div>
          ))}
        </div>
      </main>
      <Shop
        open={shopOpen}
        onClose={() => setShopOpen(false)}
        character={character}
        onPurchase={(updates) => patch(updates)}
      />
      <BrokenPrompt open={brokenOpen} onClose={() => setBrokenOpen(false)} />
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} character={character} />
      <Library
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        character={character}
        onAdd={(updates) => patch(updates)}
        onNote={(line) => patch({ notes: `${character.notes ? character.notes + "\n" : ""}${line}` })}
      />
      <Bestiary
        open={bestiaryOpen}
        onClose={() => setBestiaryOpen(false)}
        hasCharacter={!!character}
        onNote={(block) => patch({ notes: `${character.notes ? character.notes + "\n\n" : ""}${block}` })}
      />
      <CombatArena
        open={arenaOpen}
        onClose={() => { setArenaOpen(false); load(); }}
        roster={roster}
      />
      <DiceRoller character={character} />
    </div>
  );
}
